import type { DocStatus } from "@xop/dashboard-shared";

import { uploadDoc, type UploadArgs } from "./api.js";

/** Per-file state in a bulk upload run. */
export type BulkStatus = "pending" | "uploading" | "done" | "error";

export interface BulkFileState {
  name: string;
  size: number;
  status: BulkStatus;
  error?: string;
}

export interface BulkUploadOptions {
  status: DocStatus;
  categoryId?: string;
  releaseId?: string;
}

/** Title for a dropped file: filename without extension, dashes/underscores →
 * spaces. Empty (a dotfile) falls back to the raw name. The server derives the
 * same when no title is sent, but we send it so the list reads nicely. */
export function deriveTitle(filename: string): string {
  const base = filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
  return base || filename;
}

/** Merge a progress patch into a file's state, rebuilt cleanly so a stale
 * `error` is dropped once the file leaves the error status (keeps
 * `exactOptionalPropertyTypes` happy — no explicit `undefined`). Shared by the
 * Bulk-upload page and the multi-file path of the single Upload page. */
export function applyBulkPatch(s: BulkFileState, patch: Partial<BulkFileState>): BulkFileState {
  const status = patch.status ?? s.status;
  const error = status === "error" ? patch.error ?? s.error : undefined;
  return { name: s.name, size: s.size, status, ...(error ? { error } : {}) };
}

/** Upload many files SEQUENTIALLY, one Document per file, all under the chosen
 * category. Sequential (not parallel) so progress is legible and we don't hammer
 * the upload proxy. A failed file is recorded and the run continues — partial
 * success is fine and re-running skips the ones already `done`.
 *
 * `uploader` is injected for tests; defaults to the real `uploadDoc`. */
export async function runBulkUpload(
  files: readonly File[],
  opts: BulkUploadOptions,
  onProgress: (index: number, patch: Partial<BulkFileState>) => void,
  uploader: (a: UploadArgs) => Promise<unknown> = uploadDoc,
  // Skip files already uploaded in a prior run so re-running (e.g. to retry
  // failures, or after adding more) never creates duplicate documents.
  skipIndex?: (index: number) => boolean,
): Promise<{ done: number; failed: number }> {
  let done = 0;
  let failed = 0;
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (!file || skipIndex?.(i)) continue;
    onProgress(i, { status: "uploading" });
    try {
      await uploader({
        file,
        title: deriveTitle(file.name),
        status: opts.status,
        ...(opts.categoryId ? { document_category_id: opts.categoryId } : {}),
        // Same as the single upload: a pinned file is release documentation,
        // with the release's audience — see DocumentUploadPage.
        ...(opts.releaseId
          ? { default_release_id: opts.releaseId, access_scope: "release" as const, release_ids: [Number(opts.releaseId)] }
          : {}),
      });
      onProgress(i, { status: "done" });
      done++;
    } catch (e) {
      onProgress(i, {
        status: "error",
        error: e instanceof Error ? e.message : "Upload failed",
      });
      failed++;
    }
  }
  return { done, failed };
}
